class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this.modal = this;
    this.overlay = this.querySelector('.quick-view-modal__overlay');
    this.container = this.querySelector('.quick-view-modal__container');
    this.content = this.querySelector('#quick-view-content');
    this.init();
  }

  init() {
    // Close button - use event delegation since button is loaded dynamically
    if (this.content) {
      this.content.addEventListener('click', (e) => {
        const closeBtn = e.target.closest('.quick-view-modal__close');
        if (closeBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        }
      });
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Click on content background (outside product content) to close
    if (this.content) {
      this.content.addEventListener('click', (e) => {
        // Close if clicking on the content container itself (the dark background)
        // but not if clicking on the actual product content (.quick-view-content)
        const quickViewContent = e.target.closest('.quick-view-content');
        if (!quickViewContent && (e.target === this.content || e.target.classList.contains('quick-view-modal__content'))) {
          this.close();
        }
      });
    }

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('open')) {
        this.close();
      }
    });

    // Store reference to modal instance
    window.quickViewModalInstance = this;

    // Listen for quick view button clicks - use capture phase to catch before navigation
    document.addEventListener('click', (e) => {
      const quickViewBtn = e.target.closest('.product-card__quick-view-btn');
      if (quickViewBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const productHandle = quickViewBtn.dataset.productHandle;
        if (productHandle) {
          // Use stored instance or find modal
          const modalInstance = window.quickViewModalInstance || document.querySelector('quick-view-modal');
          if (modalInstance && typeof modalInstance.open === 'function') {
            modalInstance.open(productHandle);
          } else {
            console.error('Quick view modal instance not found');
          }
        }
        return false;
      }
    }, true); // Use capture phase
  }

  async open(productHandle) {
    this.modal.classList.add('open');
    document.body.classList.add('overflow-hidden');
    this.content.innerHTML = '<div class="quick-view-modal__loading"><div class="quick-view-modal__spinner"></div></div>';

    try {
      // Fetch product data using Shopify's view parameter
      const response = await fetch(`/products/${productHandle}?view=quick-view`);
      if (!response.ok) {
        throw new Error('Failed to load product');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const quickViewContent = doc.querySelector('.quick-view-content-wrapper');

      if (quickViewContent) {
        this.content.innerHTML = quickViewContent.innerHTML;
        
        // Execute any inline scripts that were in the HTML
        this.executeScripts(quickViewContent);
        
        // Wait for DOM to update
        await this.waitForDOM();
        
        // Initialize product gallery slider if it exists
        this.initGallery();
        
        // Initialize add-to-cart form for quick view (wait a bit more to ensure forms are in DOM)
        await this.waitForDOM();
        this.initAddToCartForm();
      } else {
        throw new Error('Product content not found');
      }
    } catch (error) {
      console.error('Error loading quick view:', error);
      this.content.innerHTML = `
        <div class="quick-view-modal__error">
          <p>Error loading product. Please try again.</p>
          <button class="btn" onclick="this.closest('quick-view-modal').close()">Close</button>
        </div>
      `;
    }
  }

  waitForDOM() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 100);
      });
    });
  }

  executeScripts(container) {
    // Execute any inline scripts that were in the inserted HTML
    // This ensures scripts run even when HTML is inserted via innerHTML
    const scripts = this.content.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  close() {
    this.modal.classList.remove('open');
    document.body.classList.remove('overflow-hidden');
    // Clear content after animation
    setTimeout(() => {
      this.content.innerHTML = '<div class="quick-view-modal__loading"><div class="quick-view-modal__spinner"></div></div>';
    }, 300);
  }

  initGallery() {
    const galleryMain = this.content.querySelector('.product-gallery__main');
    const galleryThumbs = this.content.querySelector('.product-gallery__thumbs');
    const gallery = this.content.querySelector('.product-gallery');
    
    if (galleryMain && typeof Swiper !== 'undefined') {
      // Destroy existing instances if any
      if (window.quickViewGalleryMain) {
        window.quickViewGalleryMain.destroy(true, true);
      }
      if (window.quickViewGalleryThumbs) {
        window.quickViewGalleryThumbs.destroy(true, true);
      }

      // Remove hidden variant slides from Swiper wrappers before init
      // (display:none slides break Swiper's spaceBetween position calculation)
      if (gallery) {
        [['product-gallery__main', 'gallery-hidden-main'], ['product-gallery__thumbs', 'gallery-hidden-thumbs']].forEach(function(pair) {
          const swiperCls = pair[0], storageCls = pair[1];
          const wrapper = gallery.querySelector('.' + swiperCls + ' .swiper-wrapper');
          if (!wrapper) return;
          let storage = gallery.querySelector('.' + storageCls);
          if (!storage) {
            storage = document.createElement('div');
            storage.className = storageCls;
            storage.style.display = 'none';
            gallery.appendChild(storage);
          }
          Array.from(wrapper.querySelectorAll('.swiper-slide')).forEach(function(slide) {
            if (slide.style.display === 'none') {
              slide.style.display = '';
              storage.appendChild(slide);
            }
          });
        });
      }

      // Initialize thumbnails first
      if (galleryThumbs) {
        window.quickViewGalleryThumbs = new Swiper(galleryThumbs, {
          slidesPerView: 4,
          spaceBetween: 12,
          allowTouchMove: true,
          grabCursor: true,
          breakpoints: {
            768: {
              slidesPerView: 6,
            },
            990: {
              slidesPerView: 6,
              spaceBetween: 16,
            },
          },
        });
      }

      // Initialize main gallery
      window.quickViewGalleryMain = new Swiper(galleryMain, {
        slidesPerView: 1,
        effect: 'slide',
        allowTouchMove: true,
        spaceBetween: 20,
        navigation: {
          nextEl: this.content.querySelector('.product-gallery__next'),
          prevEl: this.content.querySelector('.product-gallery__prev'),
        },
        breakpoints: {
          768: {
            spaceBetween: 5,
          },
        },
      });

      // Sync thumbnails with main gallery
      if (window.quickViewGalleryThumbs && window.quickViewGalleryMain) {
        window.quickViewGalleryMain.on('slideChange', function () {
          window.quickViewGalleryThumbs.slideTo(this.activeIndex);
        });

        // Click on thumbnail to change main image (use Swiper event for reliable index after DOM changes)
        window.quickViewGalleryThumbs.on('click', function (swiper, event) {
          const clickedSlide = event.target.closest('.swiper-slide');
          if (clickedSlide) {
            const index = Array.from(window.quickViewGalleryThumbs.slides).indexOf(clickedSlide);
            if (index >= 0) {
              window.quickViewGalleryMain.slideTo(index);
            }
          }
        });
      }
    }
  }

  initAddToCartForm() {
    // Get the side cart instance and initialize the new form
    const sideCartElement = document.querySelector('side-cart');
    if (sideCartElement && typeof sideCartElement.initializeNewAddToCartForms === 'function') {
      sideCartElement.initializeNewAddToCartForms();
    } else {
      // Fallback: try to get from window
      const sideCartInstance = window.sideCartInstance;
      if (sideCartInstance && typeof sideCartInstance.initializeNewAddToCartForms === 'function') {
        sideCartInstance.initializeNewAddToCartForms();
      }
    }
  }

}

customElements.define('quick-view-modal', QuickViewModal);

const initQuickView = () => {
  // The custom element will handle its own initialization
  // Just ensure the modal exists in the DOM
  const modalElement = document.querySelector('quick-view-modal');
  if (!modalElement) {
    console.warn('Quick view modal not found in DOM');
  } else {
    // Ensure the modal instance is accessible
    window.quickViewModal = modalElement;
  }
};

export { initQuickView };
